import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import {
  DensityStatCard,
  DensityBanner,
  OverviewTab,
  RoutesTab,
  ZipClustersTab,
} from './components';
import type { DensityTab } from './components';
import {
  RouteDensityStats,
  TopRoute,
  CriticalIssue,
  RouteDetail,
  ZipCluster,
} from './routeDensityTypes';
import { useRoutesStore } from '../../../store/routesStore';
import { RoutesOverviewMap } from '../../../components/RoutesOverviewMap';
import styles from './AdminRouteDensity.module.css';

const AdminRouteDensity: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DensityTab>('overview');

  const deliveryRoutes = useRoutesStore((s) => s.deliveryRoutes);
  const metrics = useRoutesStore((s) => s.metrics);
  const fetchDeliveryRoutes = useRoutesStore((s) => s.fetchDeliveryRoutes);
  const fetchMetrics = useRoutesStore((s) => s.fetchMetrics);

  useEffect(() => {
    fetchDeliveryRoutes();
    fetchMetrics();
  }, [fetchDeliveryRoutes, fetchMetrics]);

  const totalRevenue = metrics.reduce((s, m) => s + Number(m.revenue_generated || 0), 0);
  const avgRevenue = metrics.length > 0 ? totalRevenue / metrics.length : 0;
  const avgCostPer1K = metrics.length > 0
    ? metrics.reduce((s, m) => s + (Number(m.miles_driven) / Math.max(1, Number(m.revenue_generated) / 1000)), 0) / metrics.length
    : 0;

  const stats: RouteDensityStats = {
    avgRouteRevenue: `$${(avgRevenue / 1000).toFixed(1)}k`,
    avgRevenueTarget: 'Target: $15-25k/day',
    avgDensityScore: 0,
    densityTarget: 'Density score requires per-stop coordinates (coming soon)',
    avgCostPer1K: avgCostPer1K > 0 ? `$${avgCostPer1K.toFixed(0)}` : '—',
    costTarget: 'Target: $35-45',
    routesNeedRedesign: deliveryRoutes.filter((r) => r.status === 'planned').length,
    redesignNote: 'Routes still in planning state',
  };

  // Density-tier classifications, ZIP clusters, and per-route warnings need
  // dedicated analytics that aren't part of Phase 5. Render with empty data
  // so the visual shell stays intact.
  const topRoutes = useMemo<TopRoute[]>(() => [], []);
  const criticalIssues = useMemo<CriticalIssue[]>(() => [], []);
  const routeDetails = useMemo<RouteDetail[]>(() => [], []);
  const zipClusters = useMemo<ZipCluster[]>(() => [], []);
  const orphanCluster: ZipCluster = {
    id: 'orphan',
    name: 'Orphan stores (no assigned route)',
    zipCodes: '—',
    stores: 0,
    weeklyRevenue: '$0',
    avgPerStore: '$0',
    isOrphan: true,
    warning: 'Analytics service coming soon',
  };

  return (
    <div className={styles.container}>
      <div className={styles.statsGrid}>
        <DensityStatCard title="Avg Route Revenue" value={stats.avgRouteRevenue} subtitle={stats.avgRevenueTarget} icon="revenue" color="blue" />
        <DensityStatCard title="Avg Density Score" value={String(stats.avgDensityScore)} subtitle={stats.densityTarget} icon="density" color="green" valueColor="#059669" />
        <DensityStatCard title="Avg Cost per $1K" value={stats.avgCostPer1K} subtitle={stats.costTarget} icon="cost" color="orange" />
        <DensityStatCard title="Routes Need Redesign" value={String(stats.routesNeedRedesign)} subtitle={stats.redesignNote} icon="redesign" color="red" valueColor="#DC2626" />
      </div>

      <RoutesOverviewMap height={280} />

      <DensityBanner activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'overview' && (<OverviewTab topRoutes={topRoutes} criticalIssues={criticalIssues} />)}
      {activeTab === 'routes' && (<RoutesTab routes={routeDetails} />)}
      {activeTab === 'zipClusters' && (<ZipClustersTab clusters={zipClusters} orphanCluster={orphanCluster} />)}
    </div>
  );
};

export const AdminRouteDensityPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminRouteDensity />
  </ProtectedRoute>
);

export { AdminRouteDensity };
export default AdminRouteDensity;
